import { FunctionComponent, useMemo, useState } from "react";
import SubmitButton from "../../components/SubmitButton/SubmitButton";
import LinkParagraph from "../../components/LinkParagraph/LinkParagraph";
import { useParams, useLocation } from "react-router-dom";
import {
    Title,
    Info,
    SuggestionsContainer,
    SupportSuggestionContainer,
} from "./ErrorPage.style";
import { IErrorConfig } from "./types";
import SupportModal from "../../components/SupportModal/SupportModal";
import { IPage } from "../../components/PageWrapper/types";
import useIdentityNavigation from "../../common/hooks/UseIdentityNavigation";
import { asI18nKey } from "../../common/utilities/Localization";
import config, { error404Config } from "./config";
import usePageAccessGuard from "../../common/hooks/UsePageAccessGuard";
import I18n from "../../components/I18n/I18n";

const ErrorPage: FunctionComponent<IPage> = (props) => {
    const { search } = useLocation();
    const [supportModalOpen, setSupportModalOpen] = useState<boolean>(false);
    const { navigate } = useIdentityNavigation();
    const { code } = useParams<string>();

    const subType = useMemo(
        () => new URLSearchParams(search).get("subType") ?? "",
        [search]
    );

    const errorConfig = useMemo<IErrorConfig>(() => {
        if (!code) return error404Config;

        const normalizedCode = code.trim();
        const isSubTypeExists = !!subType;

        // Try to match both errorCode and subType when subType is provided
        if (isSubTypeExists) {
            const codeWithSubType = config.find(
                ({ errorCode, subType: cfgSubType }) => {
                    const regex = new RegExp(`^${errorCode}$`, "i");
                    const subTypeMatches =
                        String(cfgSubType).toLowerCase() ===
                        subType.toLowerCase();
                    return regex.test(normalizedCode) && subTypeMatches;
                }
            );
            if (codeWithSubType) return codeWithSubType;
        }
        // Fallback to matching only errorCode
        const byCodeAny = config.find(({ errorCode }) =>
            new RegExp(`^${errorCode}$`, "i").test(normalizedCode)
        );
        return byCodeAny || error404Config;
    }, [code, subType]);

    const pageNotFound = useMemo<boolean>(
        () => errorConfig === error404Config,
        [errorConfig]
    );
    usePageAccessGuard(!pageNotFound && !code);

    return (
        <>
            <Title>
                <I18n>{errorConfig?.message}</I18n>
            </Title>
            {errorConfig.info && (
                <Info>
                    <I18n>{errorConfig.info}</I18n>
                </Info>
            )}
            <SuggestionsContainer>
                <LinkParagraph
                    automationName="back_link"
                    prefix={props.translate(
                        asI18nKey("error_page.instructions")
                    )}
                    linkLabel={props.translate(
                        asI18nKey("error_page.back.action")
                    )}
                    suffix={props.translate(
                        asI18nKey("error_page.back.suffix")
                    )}
                    callback={() => window.history.back()}
                />
            </SuggestionsContainer>
            <SubmitButton automationName='submit-button' onSuccess={() => navigate("Login")}>
                {props.translate(asI18nKey("error_page.homepage.action"))}
            </SubmitButton>
            {!pageNotFound && (
                <SupportSuggestionContainer>
                    <LinkParagraph
                        automationName="support_link"
                        prefix={props.translate(
                            asI18nKey("error_page.support")
                        )}
                        linkLabel={props.translate(
                            asI18nKey("error_page.support.action")
                        )}
                        callback={() => setSupportModalOpen(true)}
                    />
                    <SupportModal
                        isOpen={supportModalOpen}
                        onClose={() => setSupportModalOpen(false)}
                        predefinedMessage={
                            code
                                ? props.translate(
                                      asI18nKey(
                                          "support.field.issue.placeholder.error"
                                      ),
                                      { code }
                                  )
                                : undefined
                        }
                    />
                </SupportSuggestionContainer>
            )}
        </>
    );
};

export default ErrorPage;
